import { submitFormJson } from 'widgets/toolbox/ajax';
import { get } from 'widgets/toolbox/util';
import { render } from 'widgets/toolbox/render';
import { TAddress } from 'widgets/forms/AddressForm';

import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';
import { TRadioSelectorInstance } from 'widgets/checkout/RadioSelector';
import { TBasketModel } from 'types/models/order';

// Delay for updating shipping methods list after form fields change in milliseconds.
// It is needed to avoid multiple requests when user fills shipping form
const DEBOUNCE_DELAY = 150;
const AUTOCOMPLETE_DEBOUNCE_DELAY = 800;

type TCustomer = {
    filteredAddressesByAllowedCountries: [
        { [key: string]: string }
    ];
    addresses: [
        { [key: string]: string }
    ];
};

/**
 * @param AddressCheckoutForm Base widget for extending
 * @returns Shipping Form widget
 */
function ShippingFormClassCreator(AddressCheckoutForm: import('widgets/checkout/AddressCheckoutForm').TAddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class ShippingForm
     * @augments AddressCheckoutForm
     * @property {string} data-select-shipping-method-url
     * @property {string} data-update-shipping-list-url
     * @classdesc Represents checkout shipping form with next features:
     * 1. Updates shipping methods list, when some of the dependencies have been changed
     * 2. Handles form submission server errors
     * 3. Toggles gift functionality
     * 4. Handles shipping method selection
     *
     * Widget has next relationship:
     * * Updates shipping methods list be calling {@link RadioSelector#updateState}
     * * Toggles gift message input by calling {@link InputTextarea#toggle}
     * * Listens event {@link InputRadio#change} from shipping methods list to handle shipping method change
     * * Listens event {@link InputCheckbox#change} from gift checkbox to toggle gift message input
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     class="b-form m-checkout_shipping"
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     data-widget-event-updateshippingform="updateSummaryEv"
     *     data-address-selector="shippingAddressSelector"
     *     data-address-fields="${pdict.shippingAddressFields.join(',')}"
     *     data-widget-event-submit="shippingSubmitted"
     *     data-select-shipping-method-url="${URLUtils.https('CheckoutShippingServices-SelectShippingMethod')}"
     *     data-update-shipping-list-url="${URLUtils.https('CheckoutShippingServices-UpdateShippingMethodsList')}"
     *     data-enabled-country-restriction="${pdict.customer.isCountryRestrictionEnabled}"
     *     action="${URLUtils.url('CheckoutShippingServices-SubmitShipping')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     data-tau="checkout_shipping"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('addressSuggestionProvider').value}"
     * >
     *     ...
     *     <div
     *         class="b-form-message b-message m-error"
     *         role="alert"
     *         hidden="hidden"
     *         data-ref="errorMessageLabel"
     *     ></div>
     *     ...
     *     <isinclude template="checkout/shipping/shipmentSelector" />
     *     ...
     *     <isinclude template="checkout/shipping/shippingAddress" />
     *     ...
     *     <isinclude template="checkout/shipping/shippingMethod" />
     *     ...
     *     <isinclude template="checkout/shipping/isGift" />
     *     ...
     * </form>
     */
    class ShippingForm extends AddressCheckoutForm {
        prevShippingData = '';
        updateShippingStateTimer: number | null = null;

        prefs() {
            return {
                updateShippingListUrl: '',
                selectShippingMethodUrl: '',
                enabledCountryRestriction: false,
                mandatoryShippingFields: '',
                ...super.prefs()
            };
        }

        /**
         * @description Activate shipping form
         * @param param - Activation params object
         * @param param.order - Order object
         * @param param.customer - Customer object
         */
        activate({ order, customer }) {
            this.order = order;
            this.customer = customer;

            this.onDestroy(() => {
                if (this.updateShippingStateTimer) {
                    window.clearTimeout(this.updateShippingStateTimer);
                    this.updateShippingStateTimer = null;
                }
            });

            if (order?.steps?.shipping?.hasErrorFields) {
                this.activateAddressForm();
                this.validate();
            } else {
                this.activateAddressSelector();
            }
        }

        /**
         * @description Update list of shipping methods after updating form fields from the server
         */
        formDataUpdated() {
            this.updateShippingState();
        }

        /**
         * @description Show errors after shipping form submission or set selected address
         * @param data - Server response object
         * @param data.cartError - Is it cart error
         * @param data.redirectUrl - Redirect url for cart Error
         * @param data.error - Is some submission errors
         * @param data.serverErrors - Common shipping step error
         * @param data.fieldErrors - Shipping form fields errors
         */
        onSubmitted(data: TServerResponse) {
            if (!data.success) {
                if (data.serverErrors && data.serverErrors.length) {
                    if (data.isGift) {
                        this.setError(data.serverErrors.join(''), false, 'errorMessageLabelBottom');
                    } else {
                        this.setError(data.serverErrors.join(''), true);
                    }
                }

                if (data.fieldErrors) {
                    this.showAddressForm();
                    this.handleFieldErrors(data.fieldErrors);
                }

                if (data.cartError && data.redirectUrl) {
                    window.location.assign(data.redirectUrl);
                }
            } else {
                const shippingAddressID = get(data, 'order.shipping.0.shippingAddress.addressUUID');

                this.setSelectedAddress(shippingAddressID);
                super.onSubmitted(data);
            }
        }

        /**
         * @description Handle backend validation field errors
         * @param fieldErrors - List with errors
         */

        handleFieldErrors(fieldErrors: TFieldError | Array<TFieldError>) {
            if (!Array.isArray(fieldErrors)) {
                fieldErrors = [fieldErrors];
            }

            fieldErrors.forEach((entries) => {
                let firstError = true;

                Object.entries(entries).forEach(([name, errorMsg]) => {
                    this.getById(name, (address: TBasicInputInstance) => {
                        if (address.id === name) {
                            if (firstError) {
                                address.setFocus();
                            }

                            firstError = false;
                            address.setError(errorMsg);
                        }
                    });
                });
            });
        }

        /**
         * @description Update address fields with new address object
         * @listens AutoCompleteField#placechanged
         * @param el - Autocomplete input field
         * @param address - Address object
         */
        handlePlaceChange(el: TBasicInputInstance, address: TAddress) {
            // Update field value silently without emitting change event
            // Do not make multiple calls to CheckoutShippingServices-UpdateShippingMethodsList with incomplete data set
            super.handlePlaceChange(el, address, true);

            // Send request to updateShippingState after shipping form updated
            this.updateShippingState();
        }

        /**
         * @description Display info message in case if user's shipment was automatically changed
         * @param isShipmentChanged - marker to define if shipment was auto-changed
         */
        handleShipmentChangedMsg(isShipmentChanged: boolean) {
            this.ref('shipmentMessageLabel').toggle(isShipmentChanged);
        }

        /**
         * @description Update shipping methods list
         * @emits ShippingForm#updateshippingform
         * @param field - Input field
         * @returns Promise object represents server response for shipping methods updating
         */
        updateShippingState(field?: TBasicInputInstance): ReturnType<typeof submitFormJson> | Promise<null> {
            if (this.updateShippingStateTimer) {
                window.clearTimeout(this.updateShippingStateTimer);
            }

            // Use longer debounce for address1 when autocomplete is active,
            // to allow handlePlaceChange to cancel and replace this call
            const isAutocompleteField = field
                && this.prefs().addressAutocompleteEnabled
                && String(field.id).split('_').pop() === 'address1';
            const delay = isAutocompleteField ? AUTOCOMPLETE_DEBOUNCE_DELAY : DEBOUNCE_DELAY;

            return new Promise((resolve) => {
                this.updateShippingStateTimer = window.setTimeout(() => {
                    this.updateShippingStateTimer = null;

                    const dataToSend = this.getFormFieldsShort();

                    // Skip request if mandatory shipping fields are not yet filled
                    const mandatoryFields = this.prefs().mandatoryShippingFields;

                    if (mandatoryFields) {
                        const fieldNameMapping: Record<string, string> = { countryCode: 'country' };
                        const hasMissingFields = mandatoryFields.split(',').some(
                            (fieldName: string) => !dataToSend[fieldNameMapping[fieldName] || fieldName]
                        );

                        if (hasMissingFields) {
                            resolve(null);

                            return;
                        }
                    }

                    // Stringify data and if they not change from previous request
                    // Skip sending request
                    const stringifiedData = JSON.stringify(dataToSend);

                    if (stringifiedData === this.prevShippingData) {
                        resolve(null);

                        return;
                    }

                    this.prevShippingData = stringifiedData;

                    this.busy();

                    submitFormJson(
                        this.prefs().updateShippingListUrl,
                        dataToSend
                    ).then((response) => {
                        if (response && response.order) {
                            this.handleShipmentChangedMsg(response.isShipmentAutoChanged);
                            this.updateState(response.order);
                            /**
                             * @description Event to update summary block
                             * @event ShippingForm#updateshippingform
                             */
                            this.emit('updateshippingform', response.order);

                            if (String(field?.id).split('_').pop() === 'stateCode') {
                                this.handleItemsRestriction(response.order);
                            }
                        }

                        resolve(response);
                    }).finally(() => {
                        this.unbusy();
                    });
                }, delay);
            });
        }

        /**
         * @description Show items restriction popup if needed
         * @param basket - Basket object
         */
        handleItemsRestriction(basket: TBasketModel) {
            const addressHasShippingRestrictions = get(basket, 'shipping.0.addressHasShippingRestrictions');

            if (addressHasShippingRestrictions) {
                render(this.ref('shippingItemsRestrictionsDialog').getText(), basket).then((content) => {
                    this.eventBus().emit('dialogshow', {
                        content: content
                    });
                });
            }
        }

        /**
         * @description Re-render shipping methods list
         * @param basket - Basket object
         */
        updateState(basket: TBasketModel) {
            this.getById('shippingMethodList', (shippingMethodList: TRadioSelectorInstance) => {
                return shippingMethodList.updateState(basket);
            });
        }

        /**
         * @description Returns selected address
         * @param filteredAddresses Filtered addresses
         * @returns Sorted addresses
         */
        getSelectedAddress(filteredAddresses: Array<Record<string, string>>): string {
            const shippingAddressUUID = this.order?.shipping?.[0]?.shippingAddress?.addressUUID;
            let selectedShippingAddress;

            if (shippingAddressUUID) {
                selectedShippingAddress = filteredAddresses.find(address => {
                    return address.addressUUID === shippingAddressUUID;
                });
            }

            if (!shippingAddressUUID) {
                selectedShippingAddress = filteredAddresses.find(address => {
                    return address.isDefaultShippingAddress;
                });
            }

            return selectedShippingAddress?.addressUUID || filteredAddresses[0].addressUUID;
        }

        /**
         * @description Sort addresses
         * @param addresses Addresses that will be sorted
         * @returns Sorted addresses
         */
        getSortedAddresses(addresses: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
            return addresses.sort((address1, address2) => {
                if ((address1.isDefaultShippingAddress && address2.addressId) || !address1.addressId) {
                    return -1;
                } else if ((address2.isDefaultShippingAddress && address1.addressId) || !address2.addressId) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }

        /**
         * @description Select shipping method handler
         * @param inputRadio - Shipping method`s input
         * @listens InputRadio#change
         * @emits ShippingForm#updateshippingform
         * @emits "checkout.update.shipping.method"
         * @returns Promise object represents server response for shipping method selection
         */
        selectShippingMethod(inputRadio: TInputRadioInstance): ReturnType<typeof submitFormJson> {
            this.busy();

            return submitFormJson(
                this.prefs().selectShippingMethodUrl,
                { methodID: inputRadio.getDataValue(), ...this.getFormFieldsShort() }
            ).then((response) => {
                if (response && response.order) {
                    this.updateOrder(response.order);
                    /**
                     * @description Event to update summary block
                     * @event ShippingForm#updateshippingform
                     */
                    this.emit('updateshippingform', response.order);
                    this.eventBus().emit(
                        'checkout.update.shipping.method',
                        response.order
                    );
                }

                return response;
            }).finally(() => {
                this.unbusy();
            });
        }

        /**
         * @description Toggle gift message input
         * @param inputCheckbox - Is gift checkbox
         * @listens InputCheckbox#change
         */
        toggleGiftWrap(inputCheckbox: TInputCheckboxInstance) {
            const checked = inputCheckbox.getValue();

            this.getById('giftMessage', (giftMessage: TInputCheckboxInstance) => {
                giftMessage.toggle(Boolean(checked));
            });
        }

        /**
         * @description Return applicable addresses for current form
         * @param order - order data
         * @param customer - customer data
         * @returns returns shipping addresses
         */
        getAddressOptions(order: unknown, customer: TCustomer): Array<Record<string, string>> {
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const customerAddresses = this.prefs().enabledCountryRestriction ? customer.filteredAddressesByAllowedCountries : customer.addresses;
            const allAddresses = [...customerAddresses || []];

            if (shippingAddress) {
                allAddresses.unshift(shippingAddress);
            }

            return allAddresses;
        }

        /**
         * @description Use as hook while shipping submit.Could be overlapped
         */
        onShippingSubmit() {
            // Placeholder for additional logic on shipping submit
        }
    }

    return ShippingForm;
}

export type TShippingForm = ReturnType<typeof ShippingFormClassCreator>;

export type TShippingFormInstance = InstanceType<TShippingForm>;

export default ShippingFormClassCreator;
